import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RoomAvailable } from '@colyseus/sdk'
import { RoomType } from '../../../types/Rooms'
import { RoomData, transformRoomData } from '../services/RoomDataTransformer'

// No need for a custom interface since we're using RoomAvailable directly
// which already has roomId, name, etc.

// Add debug flag for RoomStore logs
const isDebugRoomStore = process.env.NODE_ENV !== 'production' && localStorage.getItem('DEBUG_ROOM_STORE') === 'true'

/**
 * Keep game rooms (default + custom) for display, exclude lobby-only rooms.
 * Colyseus' real time room list includes the public lobby - we exclude it and similar.
 */
const isDisplayableRoom = (room: RoomAvailable | { roomId?: string; name?: string; metadata?: any }): boolean => {
  // SC-422: a company room (a ColyseusRoom row) is always shown, its lobby included. Lobbies now
  // have random ids and are recognised by isLobby, so a name containing "lobby" says nothing.
  if (room.metadata?.companyId && room.metadata?.customRoomId) return true

  const isLobby =
    room.metadata?.roomType === RoomType.LOBBY ||
    (room.name && String(room.name).toLowerCase().includes('lobby')) ||
    (room.roomId && String(room.roomId).toLowerCase().includes('lobby'))

  if (isLobby && !room.metadata?.isDefaultRoom) {
    return false
  }

  const isDefault = room.metadata?.isDefaultRoom === true
  const isCustom =
    (room.roomId && String(room.roomId).includes(RoomType.CUSTOM)) ||
    room.metadata?.roomType === RoomType.CUSTOM ||
    room.metadata?.roomType === 'CUSTOM' ||
    room.name === RoomType.CUSTOM

  const displayable = isDefault || (isCustom && !room.metadata?.isDefaultRoom)

  if (isDebugRoomStore) {
    console.log(
      `Room check - ID: ${room.roomId}, Name: ${room.name || 'unnamed'}, Displayable: ${displayable}`,
      room.metadata ? { metadata: room.metadata } : 'No metadata'
    )
  }

  return displayable
}

// Helper to ensure dates are serialized
const serializeDate = (dateValue: any): string => {
  if (dateValue instanceof Date) {
    return dateValue.toISOString()
  }
  if (typeof dateValue === 'string') {
    return dateValue
  }
  // Default to current time if invalid
  return new Date().toISOString()
}

const FEEDBACK_ROOM_JOINED_KEY = 'feedback_roomJoinedAt'

interface RoomState {
  lobbyJoined: boolean
  roomJoined: boolean
  roomId: string | null
  roomName: string
  roomDescription: string
  availableRooms: RoomData[]
  // Additional room data from Back4App
  spawnPoint?: { x: number; y: number }
  mapId?: string
  customRoomId?: string
  companyId?: string | null
  /** SC-422: from SEND_ROOM_DATA. Seat joining players on a free chair (the map's setting). */
  autoSeatOnJoin?: boolean
  /** SC-422: the joined room is the company's lobby. */
  isLobby?: boolean
  /** SC-422: a platform admin's room for painting the map's VRs. */
  isMapEditorRoom?: boolean
}

const initialState: RoomState = {
  lobbyJoined: false,
  roomJoined: false,
  roomId: null,
  roomName: '',
  roomDescription: '',
  availableRooms: [],
}

const roomSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    setLobbyJoined: (state, action: PayloadAction<boolean>) => {
      state.lobbyJoined = action.payload
    },
    setRoomJoined: (state, action: PayloadAction<boolean>) => {
      state.roomJoined = action.payload
      try {
        if (action.payload) {
          sessionStorage.setItem(FEEDBACK_ROOM_JOINED_KEY, String(Date.now()))
        } else {
          sessionStorage.removeItem(FEEDBACK_ROOM_JOINED_KEY)
        }
      } catch {
        /* ignore */
      }
    },
    setJoinedRoomData: (
      state,
      action: PayloadAction<{ 
        id: string; 
        name: string; 
        description: string;
        spawnPoint?: { x: number; y: number };
        mapId?: string;
        customRoomId?: string;
        companyId?: string | null;
        autoSeatOnJoin?: boolean;
        isLobby?: boolean;
        isMapEditorRoom?: boolean;
      }>
    ) => {
      state.roomId = action.payload.id
      state.roomName = action.payload.name
      state.roomDescription = action.payload.description
      // Store additional room data from Back4App
      if (action.payload.spawnPoint) {
        state.spawnPoint = action.payload.spawnPoint
      }
      if (action.payload.mapId) {
        state.mapId = action.payload.mapId
      }
      if (action.payload.customRoomId) {
        state.customRoomId = action.payload.customRoomId
      }
      if (action.payload.companyId !== undefined) {
        state.companyId = action.payload.companyId
      }
      if (typeof action.payload.autoSeatOnJoin === 'boolean') {
        state.autoSeatOnJoin = action.payload.autoSeatOnJoin
      }
      if (typeof action.payload.isLobby === 'boolean') {
        state.isLobby = action.payload.isLobby
      }
      if (typeof action.payload.isMapEditorRoom === 'boolean') {
        state.isMapEditorRoom = action.payload.isMapEditorRoom
      } else if (!action.payload.id) {
        // Leaving clears it, so the VR poll doesn't carry over into the next room.
        state.isMapEditorRoom = false
      }
    },
    setAvailableRooms: (state, action: PayloadAction<RoomAvailable[]>) => {
      state.availableRooms = action.payload
        .filter(isDisplayableRoom)
        .map(room => transformRoomData(room))
    },
    addAvailableRooms: (state, action: PayloadAction<{ roomId: string; room: RoomAvailable }>) => {
      const room = action.payload.room;

      // Debug log the raw room data
      if (isDebugRoomStore) {
        console.log(`Processing room in store: ${room.roomId}`, {
          name: room.name,
          metadata: room.metadata,
          hasFullData: !!room.metadata,
        });
      }

      if (!isDisplayableRoom(room)) {
        if (isDebugRoomStore) {
          console.log(`Skipping non-custom room: ${room.roomId}`);
        }
        return;
      }

      const roomIndex = state.availableRooms.findIndex(
        (r) => r.roomId === action.payload.roomId
      );

      const roomData = transformRoomData(room);

      // Deep equality check to prevent unnecessary updates
      if (roomIndex !== -1) {
        const existingRoom = state.availableRooms[roomIndex];
        // Compare key fields to determine if update is needed
        const hasChanges = 
          existingRoom.name !== roomData.name ||
          existingRoom.description !== roomData.description ||
          existingRoom.clients !== roomData.clients ||
          existingRoom.maxClients !== roomData.maxClients ||
          existingRoom.hasPassword !== roomData.hasPassword ||
          existingRoom.createdAt !== roomData.createdAt ||
          existingRoom.updatedAt !== roomData.updatedAt;
        
        // Only update if there are actual changes
        if (hasChanges) {
          state.availableRooms[roomIndex] = roomData;
        }
      } else {
        state.availableRooms.push(roomData);
      }
    },
    removeAvailableRooms: (state, action: PayloadAction<string>) => {
      state.availableRooms = state.availableRooms.filter((room) => room.roomId !== action.payload)
    },
    setRoomId: (state, action: PayloadAction<string | null>) => {
      state.roomId = action.payload
    },
    setRoomInfo: (state, action: PayloadAction<{ name: string; description: string }>) => {
      state.roomName = action.payload.name
      state.roomDescription = action.payload.description
    },
    setDiscoveryRooms: (state, action: PayloadAction<RoomAvailable[]>) => {
      // Set rooms from discovery service (replaces current room list)
      state.availableRooms = action.payload
        .filter(isDisplayableRoom)
        .map(room => transformRoomData(room))
    },
    addResolvedRoom: (state, action: PayloadAction<{ roomId: string; name?: string; companyId?: string | null; customRoomId?: string; metadata?: Record<string, unknown> }>) => {
      const r = action.payload
      const roomIndex = state.availableRooms.findIndex((x) => x.roomId === r.roomId)
      const roomData: RoomData = {
        id: r.roomId,
        roomId: r.roomId,
        roomType: (r.metadata?.roomType as RoomType) || RoomType.CUSTOM,
        name: r.name || 'Room',
        description: (r.metadata?.description as string) || '',
        creatorName: (r.metadata?.creatorName as string) || 'Unknown',
        hasPassword: !!(r.metadata?.hasPassword || r.metadata?.isPrivate || r.metadata?.passwordProtected),
        isPrivate: !!(r.metadata?.isPrivate || r.metadata?.hasPassword),
        passwordProtected: !!(r.metadata?.passwordProtected || r.metadata?.hasPassword),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        clients: (r.metadata?.members as number) ?? 0,
        maxClients: r.metadata?.maxClients as number | undefined,
        metadata: r.metadata || {},
      }
      if (roomIndex !== -1) {
        state.availableRooms[roomIndex] = roomData
      } else {
        state.availableRooms.push(roomData)
      }
    },
  },
})

export const {
  setLobbyJoined,
  setRoomJoined,
  setJoinedRoomData,
  setAvailableRooms,
  addAvailableRooms,
  removeAvailableRooms,
  setRoomId,
  setRoomInfo,
  setDiscoveryRooms,
  addResolvedRoom,
} = roomSlice.actions

export default roomSlice.reducer
